document.documentElement.classList.add('js')

/*
  Section assets
*/
function loadSectionAssets (scope = document) {
  const sectionElements = []

  if (scope instanceof Element && scope.matches('[data-section-css], [data-section-js]')) {
    sectionElements.push(scope)
  }

  if (scope.querySelectorAll) {
    sectionElements.push(...scope.querySelectorAll('[data-section-css], [data-section-js]'))
  }

  sectionElements.forEach((element) => {
    element.dataset.sectionCss?.split(',').forEach((href) => {
      const assetUrl = href.trim()
      const absoluteAssetUrl = new URL(assetUrl, document.baseURI).href

      if (!assetUrl || Array.from(document.querySelectorAll('link[rel~="stylesheet"]')).some((link) => link.href === absoluteAssetUrl)) return

      const link = document.createElement('link')

      link.href = assetUrl
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    })

    element.dataset.sectionJs?.split(',').forEach((src) => {
      const assetUrl = src.trim()
      const absoluteAssetUrl = new URL(assetUrl, document.baseURI).href

      if (!assetUrl || Array.from(document.scripts).some((script) => script.src === absoluteAssetUrl)) return

      const script = document.createElement('script')

      script.src = assetUrl
      script.defer = true
      document.head.appendChild(script)
    })
  })
}

function initSectionAssets () {
  loadSectionAssets()

  document.addEventListener('shopify:section:load', (event) => {
    loadSectionAssets(event.target)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSectionAssets, { once: true })
} else {
  initSectionAssets()
}

/*
  Page title animation
*/
function initPageTitleAnimation () {
  const isDesignMode = document.documentElement.dataset.designMode === 'true'
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  const fullTitle = document.title.trim()

  if (isDesignMode || fullTitle.length < 2) return

  const titleWords = fullTitle.split(/\s+/)
  const wordDelay = 1000
  const completedTitleDelay = 2400
  const restartDelay = 500
  let wordIndex = 1
  let animationTimer

  function showNextWord () {
    if (!document.hidden || reduceMotionQuery.matches) {
      stopAnimation()
      return
    }

    document.title = titleWords.slice(0, wordIndex).join(' ')
    wordIndex += 1

    if (wordIndex <= titleWords.length) {
      animationTimer = window.setTimeout(showNextWord, wordDelay)
      return
    }

    animationTimer = window.setTimeout(() => {
      wordIndex = 1
      showNextWord()
    }, completedTitleDelay + restartDelay)
  }

  function stopAnimation () {
    window.clearTimeout(animationTimer)
    animationTimer = null
    wordIndex = 1
    document.title = fullTitle
  }

  function startAnimation () {
    if (!document.hidden || reduceMotionQuery.matches || animationTimer) return

    animationTimer = window.setTimeout(showNextWord, completedTitleDelay)
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      startAnimation()
    } else {
      stopAnimation()
    }
  })

  reduceMotionQuery.addEventListener('change', (event) => {
    if (event.matches) {
      stopAnimation()
    } else {
      startAnimation()
    }
  })

  window.addEventListener('pagehide', stopAnimation, { once: true })
  startAnimation()
}

initPageTitleAnimation()

/*
  Viewport animations
*/
function initViewportAnimations () {
  const observedElements = new Set()
  const animationSelector = '[class*="scroll-animate-"]'
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  const isDesignMode = document.documentElement.dataset.designMode === 'true'

  function getViewportElements (scope = document) {
    const elements = []

    if (scope instanceof Element && scope.matches('[data-viewport-entered]')) {
      elements.push(scope)
    }

    if (scope.querySelectorAll) {
      elements.push(...scope.querySelectorAll('[data-viewport-entered]'))
    }

    return elements
  }

  function setChildAnimationIndexes (element) {
    const children = Array.from(element.children)
    const animationChildren = element.hasAttribute('data-animate-children')
      ? children
      : children.filter((child) => child.matches(animationSelector))

    animationChildren.forEach((child, index) => {
      child.style.setProperty('--animation-index', index)
    })
  }

  function enterViewport (element) {
    setChildAnimationIndexes(element)
    element.dataset.viewportEntered = 'true'
    observedElements.delete(element)
  }

  if (isDesignMode || reduceMotionQuery.matches || !('IntersectionObserver' in window)) {
    function enterViewportElements (scope = document) {
      getViewportElements(scope).forEach((element) => {
        if (element.dataset.animateOnScroll === 'false') return

        enterViewport(element)
      })
    }

    enterViewportElements()

    if (isDesignMode && 'MutationObserver' in window) {
      const designModeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return

            enterViewportElements(node)
          })
        })
      })

      designModeObserver.observe(document.body, { childList: true, subtree: true })
    }

    return
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return

      enterViewport(entry.target)
      observer.unobserve(entry.target)
    })
  }, { rootMargin: '0px 0px -60px 0px' })

  function observeViewportElements (scope = document) {
    getViewportElements(scope).forEach((element) => {
      if (observedElements.has(element)) return
      if (element.dataset.animateOnScroll === 'false') return

      observedElements.add(element)
      observer.observe(element)
    })
  }

  observeViewportElements()

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return

        observeViewportElements(node)
      })
    })
  })

  mutationObserver.observe(document.body, { childList: true, subtree: true })
}

initViewportAnimations()

/*
  SVG inline utility
*/
async function inlineSvgImages () {
  const images = document.querySelectorAll('img[data-transform-svg-inline]')

  return Promise.all(
    Array.from(images).map(async (image) => {
      const source = image.currentSrc || image.getAttribute('src') || ''

      if (image.dataset.inlineSvgProcessed === 'true' || !source.split('?')[0].toLowerCase().endsWith('.svg')) {
        return null
      }

      try {
        const response = await fetch(source, { credentials: 'same-origin' })

        if (!response.ok) return null

        const markup = await response.text()
        const svgDocument = new DOMParser().parseFromString(markup, 'image/svg+xml')
        const svg = svgDocument.querySelector('svg')

        if (!svg) return null

        const copiedAttributes = ['class', 'height', 'id', 'style', 'width']

        copiedAttributes.forEach((attribute) => {
          const value = image.getAttribute(attribute)

          if (value) svg.setAttribute(attribute, value)
        })

        const alt = image.getAttribute('alt')

        if (alt) {
          svg.setAttribute('role', 'img')
          svg.setAttribute('aria-label', alt)
        } else {
          svg.setAttribute('aria-hidden', 'true')
        }

        if (svg.hasAttribute('fill')) {
          const fill = (svg.getAttribute('fill') || '').trim().toLowerCase()

          if (fill && fill !== 'none') svg.setAttribute('fill', 'currentColor')
        }

        svg.querySelectorAll('[fill]').forEach((element) => {
          const fill = (element.getAttribute('fill') || '').trim().toLowerCase()

          if (fill && fill !== 'none') element.setAttribute('fill', 'currentColor')
        })

        image.dataset.inlineSvgProcessed = 'true'
        image.replaceWith(svg)
        return svg
      } catch (error) {
        console.error('Error inlining SVG:', error)
        return null
      }
    })
  )
}

inlineSvgImages()
document.addEventListener('shopify:section:load', inlineSvgImages)

/*
  Sticky header
*/
function initStickyHeaders (scope = document) {
  const headers = []

  if (scope instanceof Element && scope.matches('[data-sticky-header]')) {
    headers.push(scope)
  }

  if (scope.querySelectorAll) {
    headers.push(...scope.querySelectorAll('[data-sticky-header]'))
  }

  headers.forEach((header) => {
    if (header.stickyObserver) return

    const sentinel = header.previousElementSibling

    if (!sentinel?.matches('[data-header-sticky-sentinel]')) return

    const updateStickyHeaderHeight = () => {
      document.documentElement.style.setProperty('--sticky-header-height', `${header.offsetHeight}px`)
    }

    header.stickyObserver = new IntersectionObserver(([entry]) => {
      header.classList.toggle('is-sticky', !entry.isIntersecting)
    })
    header.stickyResizeObserver = new ResizeObserver(updateStickyHeaderHeight)
    header.stickyObserver.observe(sentinel)
    header.stickyResizeObserver.observe(header)
    updateStickyHeaderHeight()
  })
}

initStickyHeaders()

document.addEventListener('shopify:section:load', (event) => {
  initStickyHeaders(event.target)
})

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-sticky-header]').forEach((header) => {
    header.stickyObserver?.disconnect()
    header.stickyResizeObserver?.disconnect()
    header.stickyObserver = null
    header.stickyResizeObserver = null
  })
})
